import React, { useState, useEffect } from 'react';
import { 
  ShieldAlert, 
  CheckCircle2, 
  MoreHorizontal, 
  ExternalLink, 
  Trash2, 
  Eye, 
  Filter,
  MessageSquare,
  Flag
} from 'lucide-react';
import { cn } from '../../utils';
import { postService } from '../../lib/postService';
import MediaRender from '../../components/MediaRender';

const ContentModeration = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const posts = await postService.getPosts();
        // Map posts to a "reported" format for the admin dashboard mockup
        const mockReports = posts.map(post => ({
          id: post.id,
          user: post.author?.username || 'unknown',
          type: post.media_type === 'video' ? 'Video' : post.image_url ? 'Image' : 'Text',
          content: post.content || 'No text content',
          reason: 'User Flagged',
          severity: post.image_url ? 'High' : 'Medium',
          timestamp: new Date(post.created_at).toLocaleString(),
          image: post.image_url,
          video: post.video_url
        }));
        setReports(mockReports);
      } catch (error) {
        console.error("Failed to fetch reports:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, []);

  const handleRemove = async (id) => {
    try {
      await postService.deletePost(id);
      setReports(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error("Failed to delete post:", error);
      alert("Failed to delete post");
    }
  };

  const handleApprove = (id) => {
    // In a real app, this would clear the reports flag without deleting the post
    setReports(prev => prev.filter(r => r.id !== id));
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Content Moderation</h1>
          <p className="text-white/40 mt-1 text-sm">Review reported content and maintain community guidelines.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-rose-500/15 text-rose-400 rounded-xl font-bold text-sm border border-rose-500/25">
            {reports.length} Pending Reports
          </div>
          <button className="p-2 glass-panel border border-white/10 rounded-xl text-white/40 hover:text-white hover:border-white/30 transition-all">
            <Filter size={20} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-500">Loading reports...</div>
      ) : reports.length === 0 ? (
        <div className="py-12 text-center text-slate-500">No pending reports! Good job.</div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {reports.map((item) => (
            <div
              key={item.id}
              className={`admin-card rounded-3xl overflow-hidden hover:scale-[1.01] transition-all duration-300 group ${
                item.severity === 'High' ? 'border-rose-500/25' : ''
              }`}
            >
              <div className="p-6 flex flex-col md:flex-row gap-6">
                {/* Content Preview */}
                {item.image || item.video ? (
                  <div className="w-full md:w-48 h-48 rounded-2xl overflow-hidden flex-shrink-0 relative bg-black">
                    {item.video ? (
                      <MediaRender type="video" src={item.video} className="w-full h-full object-cover opacity-80" />
                    ) : (
                      <MediaRender type="image" src={item.image} alt="Reported content" className="w-full h-full object-cover opacity-80" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Eye className="text-white" size={24} />
                    </div>
                  </div>
                ) : (
                  <div className="w-full md:w-48 h-48 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 border-2 border-dashed border-slate-200 dark:border-slate-700">
                    <MessageSquare size={32} className="text-slate-300" />
                  </div>
                )}

                {/* Details */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={cn(
                          "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider",
                          item.type === 'Text' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                          "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                        )}>
                          {item.type}
                        </span>
                        <span className="text-slate-300 dark:text-slate-700">•</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{item.timestamp}</span>
                      </div>
                      <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                        Reported by @{item.user}
                        <ExternalLink size={14} className="text-slate-400 cursor-pointer hover:text-indigo-600" />
                      </h3>
                    </div>
                    <div className={cn(
                      "px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5",
                      item.severity === 'High' ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" :
                      "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                    )}>
                      <Flag size={12} />
                      {item.severity} Priority
                    </div>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                    <p className="text-sm text-slate-700 dark:text-slate-300 italic">"{item.content}"</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      <ShieldAlert size={14} className="text-rose-500" />
                      Reason: <span className="text-slate-900 dark:text-slate-200">{item.reason}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex md:flex-col justify-end gap-2 border-t md:border-t-0 md:border-l border-white/10 pt-4 md:pt-0 md:pl-6">
                  <button onClick={() => handleApprove(item.id)} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 rounded-xl font-bold text-sm hover:bg-emerald-500/25 transition-all">
                    <CheckCircle2 size={18} />
                    Approve
                  </button>
                  <button onClick={() => handleRemove(item.id)} className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-rose-500/15 border border-rose-500/25 text-rose-400 rounded-xl font-bold text-sm hover:bg-rose-500/25 transition-all">
                    <Trash2 size={18} />
                    Remove
                  </button>
                  <button className="flex-1 md:flex-none flex items-center justify-center p-2 text-white/30 hover:text-white transition-colors">
                    <MoreHorizontal size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentModeration;
